import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Phone, Mail, Cake, Shield, CalendarDays, Stethoscope, UserCircle, FolderOpen } from 'lucide-react';
import { patientAPI } from '@/lib/api';
import { formatDateEs } from '@/lib/utils';

// Ficha rápida del paciente en OVERLAY: se abre encima de cualquier página
// de la consola sin navegar. Disparo: openPatientQuickView(patientId).
export function openPatientQuickView(patientId) {
  window.dispatchEvent(new CustomEvent('open-patient-quickview', { detail: { patientId } }));
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5 py-1.5">
    <Icon size={15} className="text-slate-400 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 break-words">{value || <span className="text-slate-400">No registrado</span>}</div>
    </div>
  </div>
);

export default function PatientQuickViewDialog() {
  const navigate = useNavigate();
  const [patientId, setPatientId] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => setPatientId(e.detail?.patientId || null);
    window.addEventListener('open-patient-quickview', handler);
    return () => window.removeEventListener('open-patient-quickview', handler);
  }, []);

  useEffect(() => {
    if (!patientId) { setPatient(null); return; }
    setLoading(true);
    patientAPI.getOne(patientId)
      .then((r) => setPatient(r.data))
      .catch(() => setPatient(null))
      .finally(() => setLoading(false));
  }, [patientId]);

  const close = () => setPatientId(null);
  const goFull = () => { close(); navigate(`/admin/patients?open=${patientId}`); };
  const goHistory = () => { close(); navigate(`/admin/clinical-history?patient=${patientId}`); };

  return (
    <Dialog open={!!patientId} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-md" data-testid="patient-quickview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap text-base">
            <UserCircle size={19} className="text-brand-deep" />
            {loading ? 'Cargando…' : (patient?.name || 'Paciente')}
            {patient?.nhc && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[11px]">
                {patient.nhc}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : patient ? (
          <>
            <div className="grid grid-cols-2 gap-x-4">
              <Row icon={Phone} label="Teléfono" value={patient.phone} />
              <Row icon={Mail} label="Email" value={patient.email} />
              <Row icon={Cake} label="Fecha de nacimiento"
                value={patient.birthdate ? formatDateEs(patient.birthdate) : null} />
              <Row icon={Shield} label="Aseguradora" value={patient.insurance_name} />
              <Row icon={CalendarDays} label="Citas / Última visita"
                value={`${patient.total_appointments ?? 0} citas${patient.last_visit ? ` · ${formatDateEs(patient.last_visit)}` : ''}`} />
              <Row icon={Stethoscope} label="Cirugías" value={String(patient.total_surgeries ?? 0)} />
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={goFull} className="flex-1 bg-brand-navy hover:bg-brand-deep text-white" size="sm" data-testid="quickview-full-file-btn">
                <UserCircle size={14} className="mr-1.5" /> Ficha completa
              </Button>
              <Button onClick={goHistory} variant="outline" className="flex-1" size="sm" data-testid="quickview-history-btn">
                <FolderOpen size={14} className="mr-1.5" /> Historia clínica
              </Button>
            </div>
          </>
        ) : (
          <div className="py-6 text-center text-sm text-slate-500">No se pudo cargar la ficha</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
